/**
 * Duplicate Messages cleanup view — Phase 2.3.j.5.
 *
 * Owns the "Duplicate messages" tab inside the Tools view: scans pending
 * messages for duplicate groupings, lets the operator select one or more
 * groups, and bulk-purges the selected duplicates.
 *
 * Loaded as a module before admin.js. The IIFE registers
 * `window.adminViews.duplicateMessages`. The Tools view-router callback
 * (still in admin.js) calls `state` to read the loaded flag and
 * `load`/`render` accordingly.
 */

// Phase 3.2.c: replaced `configure({helpers})` injection with direct imports.
import { escapeHtml, formatDateTimeFull } from '../../src/lib/format.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const adminAPI = api;
const API = api.routes;
const showFlash = (msg, type, ms) => notify.showFlash(msg, type, ms);

  // ---- DOM refs (lazy-resolved) ---------------------------------------
  let duplicateMessagesRefreshBtn, duplicateMessagesDeleteBtn,
      duplicateMessagesStatus, duplicateMessagesTbody;

  let domResolved = false;
  function resolveDom() {
    if (domResolved) return;
    domResolved = true;
    duplicateMessagesRefreshBtn = document.getElementById('duplicateMessagesRefreshBtn');
    duplicateMessagesDeleteBtn = document.getElementById('duplicateMessagesDeleteBtn');
    duplicateMessagesStatus = document.getElementById('duplicateMessagesStatus');
    duplicateMessagesTbody = document.getElementById('duplicateMessagesTbody');
  }

  // ---- State ----------------------------------------------------------
  const duplicateCleanupState = {
    loading: false,
    loadingPromise: null,
    loaded: false,
    groups: [],
    selection: new Set(),
    status: '',
    error: null,
  };

    function describeDuplicateSkipReason(reason) {
      switch (reason) {
        case 'not_found':
          return 'not found';
        case 'not_pending':
          return 'not pending';
        case 'locked':
          return 'locked';
        case 'linked_to_ticket':
          return 'linked to a ticket';
        default:
          return reason || 'skipped';
      }
    }

    function renderDuplicateMessages() {
      if (!duplicateMessagesTbody) return;
      const { loading, loaded, groups, selection, status } = duplicateCleanupState;
      if (duplicateMessagesStatus) {
        duplicateMessagesStatus.textContent = status || (loading ? 'Scanning for duplicate messages…' : '');
        duplicateMessagesStatus.hidden = !(duplicateMessagesStatus.textContent);
      }
      if (duplicateMessagesRefreshBtn) {
        duplicateMessagesRefreshBtn.disabled = loading;
        duplicateMessagesRefreshBtn.textContent = loading ? 'Scanning…' : 'Scan for duplicates';
      }
      if (duplicateMessagesDeleteBtn) {
        const selectionCount = selection.size;
        duplicateMessagesDeleteBtn.disabled = loading || selectionCount === 0;
        if (loading) {
          duplicateMessagesDeleteBtn.textContent = 'Working…';
        } else if (selectionCount > 0) {
          duplicateMessagesDeleteBtn.textContent = `Delete selected (${selectionCount})`;
        } else {
          duplicateMessagesDeleteBtn.textContent = 'Delete selected';
        }
      }

      if (loading) {
        duplicateMessagesTbody.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">Scanning for duplicate messages…</td></tr>';
        return;
      }
      if (!loaded) {
        duplicateMessagesTbody.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">Scan to identify duplicate pending messages.</td></tr>';
        return;
      }
      if (!groups.length) {
        duplicateMessagesTbody.innerHTML = '<tr><td colspan="7" style="color:var(--sub);">No duplicate pending messages detected.</td></tr>';
        return;
      }

      const rows = [];
      groups.forEach((group) => {
        const groupKey = String(group.key || '');
        const escapedGroupKey = escapeHtml(groupKey);
        const channelLabel = (group.channel || '').toString().toUpperCase() || 'UNKNOWN';
        const identifier = group.message_id || '(no message id)';
        const reasonLabel = group.reason === 'message_id'
          ? 'Same message ID'
          : group.reason === 'pbx_call'
            ? 'Same PBX call'
            : 'Unclassified duplicate';
        const metaParts = [];
        if (group.mailbox_key) {
          metaParts.push(`Mailbox: ${group.mailbox_key}`);
        }
        metaParts.push(reasonLabel);
        metaParts.push(`${group.items.length} message${group.items.length === 1 ? '' : 's'}`);
        const metaText = metaParts.map((part) => escapeHtml(String(part))).join(' • ');
        rows.push(
          `<tr class="duplicate-group-row" data-group-key="${escapedGroupKey}">
            <td colspan="7">
              <div class="duplicate-group-header">
                <div class="duplicate-group-title">
                  <strong>${escapeHtml(channelLabel)}</strong>
                  <div class="duplicate-group-meta">Identifier: ${escapeHtml(String(identifier))}</div>
                  ${group.mailbox_key ? `<div class="duplicate-group-meta">Mailbox: ${escapeHtml(String(group.mailbox_key))}</div>` : ''}
                  <div class="duplicate-group-meta">${metaText}</div>
                </div>
                <div class="duplicate-group-actions">
                  <button type="button" class="btn compact" data-duplicate-action="select-older" data-group-key="${escapedGroupKey}">Select older</button>
                  <button type="button" class="btn compact" data-duplicate-action="select-all" data-group-key="${escapedGroupKey}">Select all</button>
                  <button type="button" class="btn compact ghost" data-duplicate-action="clear-group" data-group-key="${escapedGroupKey}">Clear group</button>
                </div>
              </div>
            </td>
          </tr>`,
        );
        group.items.forEach((item, index) => {
          const id = String(item.id);
          const selected = selection.has(id);
          const rowClasses = ['duplicate-row'];
          if (selected) rowClasses.push('selected');
          const statusLabel = item.status ? item.status.replace(/_/g, ' ') : '';
          const createdLabel = item.created_at ? formatDateTimeFull(item.created_at) : '';
          const indicator = index === 0 ? 'Newest' : 'Older';
          const itemIdentifier = item.message_id || identifier || '(unknown)';
          rows.push(
            `<tr class="${rowClasses.join(' ')}" data-group-key="${escapedGroupKey}" data-message-id="${escapeHtml(id)}">
              <td><input type="checkbox" class="duplicate-select" data-message-id="${escapeHtml(id)}"${duplicateCleanupState.loading ? ' disabled' : ''}${selected ? ' checked' : ''}></td>
              <td>${escapeHtml(channelLabel)}</td>
              <td>${escapeHtml(String(itemIdentifier))}</td>
              <td>${item.customer_contact ? escapeHtml(String(item.customer_contact)) : '—'}</td>
              <td>${item.subject ? escapeHtml(String(item.subject)) : '—'}</td>
              <td>${createdLabel ? escapeHtml(createdLabel) : '—'}</td>
              <td>${statusLabel ? escapeHtml(statusLabel) : '—'} <span class="duplicate-indicator">${escapeHtml(indicator)}</span></td>
            </tr>`,
          );
        });
      });
      duplicateMessagesTbody.innerHTML = rows.join('');
    }

    async function loadDuplicateMessages() {
      if (duplicateCleanupState.loading) return;
      duplicateCleanupState.loading = true;
      duplicateCleanupState.error = null;
      duplicateCleanupState.status = 'Scanning for duplicate messages…';
      renderDuplicateMessages();
      try {
        const params = new URLSearchParams();
        params.append('status', 'pending');
        params.set('max_records', '2000');
        const payload = await adminAPI.fetch(API.adminPendingMessageDuplicates(params.toString()), { method: 'GET' });
        const groups = Array.isArray(payload && payload.groups) ? payload.groups : [];
        duplicateCleanupState.groups = groups
          .map((group) => ({
            key: group.key || '',
            reason: group.reason || 'unknown',
            channel: group.channel || '',
            mailbox_key: group.mailbox_key || null,
            message_id: group.message_id || null,
            items: Array.isArray(group.items)
              ? group.items.map((item) => ({
                ...item,
                id: String(item.id),
                message_id: item.message_id || group.message_id || null,
              }))
              : [],
          }))
          .filter((group) => group.items.length > 0);
        duplicateCleanupState.selection = new Set();
        duplicateCleanupState.groups.forEach((group) => {
          group.items.forEach((item, index) => {
            if (index > 0) {
              duplicateCleanupState.selection.add(String(item.id));
            }
          });
        });
        duplicateCleanupState.loaded = true;
        const groupCount = duplicateCleanupState.groups.length;
        const messageCount = duplicateCleanupState.groups.reduce((sum, group) => sum + group.items.length, 0);
        duplicateCleanupState.status = groupCount
          ? `Detected ${groupCount} duplicate group${groupCount === 1 ? '' : 's'} covering ${messageCount} message${messageCount === 1 ? '' : 's'}. Older messages are preselected for removal.`
          : 'No duplicate pending messages detected.';
      } catch (err) {
        duplicateCleanupState.error = err;
        const detail = err && err.message ? err.message : 'Unable to scan duplicates.';
        duplicateCleanupState.status = detail;
        showFlash(detail, 'error');
      } finally {
        duplicateCleanupState.loading = false;
        renderDuplicateMessages();
      }
    }

    async function purgeDuplicateMessages() {
      if (duplicateCleanupState.loading) return;
      const ids = Array.from(duplicateCleanupState.selection);
      if (!ids.length) {
        showFlash('Select duplicate messages to delete.', 'error');
        return;
      }
      const confirmDelete = window.confirm(`Delete ${ids.length} duplicate message${ids.length === 1 ? '' : 's'}? This cannot be undone.`);
      if (!confirmDelete) return;
      duplicateCleanupState.loading = true;
      duplicateCleanupState.status = 'Deleting selected duplicates…';
      renderDuplicateMessages();
      let deletionSucceeded = false;
      try {
        const response = await adminAPI.fetch(API.adminPendingMessageDuplicatesPurge, {
          method: 'POST',
          body: JSON.stringify({ message_ids: ids }),
        });
        const deletedIds = Array.isArray(response && response.deleted_ids) ? response.deleted_ids : [];
        const skippedItems = Array.isArray(response && response.skipped) ? response.skipped : [];
        if (deletedIds.length) {
          showFlash(`Deleted ${deletedIds.length} duplicate message${deletedIds.length === 1 ? '' : 's'}.`, 'success');
        } else {
          showFlash('No duplicates were deleted.', 'warn');
        }
        if (skippedItems.length) {
          const summary = skippedItems.reduce((acc, item) => {
            const label = describeDuplicateSkipReason(item && item.reason);
            acc[label] = (acc[label] || 0) + 1;
            return acc;
          }, {});
          const parts = Object.entries(summary)
            .map(([label, count]) => `${count} ${label}`)
            .join(', ');
          showFlash(`Skipped ${skippedItems.length} message${skippedItems.length === 1 ? '' : 's'} (${parts}).`, 'warn');
        }
        deletionSucceeded = true;
      } catch (err) {
        const detail = err && err.message ? err.message : 'Unable to delete duplicates.';
        duplicateCleanupState.status = detail;
        showFlash(detail, 'error');
      } finally {
        duplicateCleanupState.loading = false;
        if (deletionSucceeded) {
          duplicateCleanupState.selection.clear();
          await loadDuplicateMessages();
        } else {
          renderDuplicateMessages();
        }
      }
    }

    function handleDuplicateSelectionChange(event) {
      const target = event.target;
      if (!target || !target.classList || !target.classList.contains('duplicate-select')) return;
      const messageId = target.dataset.messageId ? String(target.dataset.messageId) : '';
      if (!messageId) return;
      if (target.checked) {
        duplicateCleanupState.selection.add(messageId);
      } else {
        duplicateCleanupState.selection.delete(messageId);
      }
      renderDuplicateMessages();
    }

    function handleDuplicateGroupClick(event) {
      const btn = event.target && event.target.closest ? event.target.closest('[data-duplicate-action]') : null;
      if (!btn) return;
      event.preventDefault();
      const action = btn.dataset.duplicateAction;
      const groupKey = btn.dataset.groupKey ? String(btn.dataset.groupKey) : '';
      if (!groupKey) return;
      const group = duplicateCleanupState.groups.find((item) => String(item.key) === groupKey);
      if (!group) return;
      if (action === 'select-older') {
        group.items.forEach((item, index) => {
          const id = String(item.id);
          if (index > 0) {
            duplicateCleanupState.selection.add(id);
          } else {
            duplicateCleanupState.selection.delete(id);
          }
        });
      } else if (action === 'select-all') {
        group.items.forEach((item) => duplicateCleanupState.selection.add(String(item.id)));
      } else if (action === 'clear-group') {
        group.items.forEach((item) => duplicateCleanupState.selection.delete(String(item.id)));
      }
      renderDuplicateMessages();
    }

  function attachListeners() {
    resolveDom();
    if (duplicateMessagesRefreshBtn) {
      duplicateMessagesRefreshBtn.addEventListener('click', (event) => {
        event.preventDefault();
        loadDuplicateMessages();
      });
    }
    if (duplicateMessagesDeleteBtn) {
      duplicateMessagesDeleteBtn.addEventListener('click', (event) => {
        event.preventDefault();
        purgeDuplicateMessages().catch(() => {});
      });
    }
    if (duplicateMessagesTbody) {
      duplicateMessagesTbody.addEventListener('change', handleDuplicateSelectionChange);
      duplicateMessagesTbody.addEventListener('click', handleDuplicateGroupClick);
    }
    renderDuplicateMessages();
  }

export default {
  attachListeners,
  state: duplicateCleanupState,
  load: loadDuplicateMessages,
  render: renderDuplicateMessages,
};
